import { useEffect, useState } from 'react';
import {
  getScanAnalysisSettings,
  updateScanAnalysisSettings,
  type ScanAnalysisMode,
  type ScanAnalysisSettings,
} from '../lib/api';
import { theme } from '../theme';

const MODE_OPTIONS: Array<{
  value: ScanAnalysisMode;
  label: string;
  description: string;
}> = [
  {
    value: 'heuristic',
    label: 'Heuristic (default)',
    description:
      'Only scans the heuristic flags as thin (few live sensors, OBDb matched but no readings, etc.) are sent for AI analysis.',
  },
  {
    value: 'all',
    label: 'All scans',
    description:
      'Every scan is enqueued for AI analysis. The heuristic still runs and stamps the review trigger reason so staff/LLM can see why each scan was thin. Cost-heavy: ~$0.001 per scan.',
  },
  {
    value: 'none',
    label: 'Disabled',
    description:
      'Automatic enqueueing is paused. Manual analysis requests from a scan detail page still work — this only affects the automatic pipeline.',
  },
];

export function ScanAnalysisSettingsPanel() {
  const [settings, setSettings] = useState<ScanAnalysisSettings | null>(null);
  const [pendingMode, setPendingMode] = useState<ScanAnalysisMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    getScanAnalysisSettings()
      .then((res) => {
        setSettings(res);
        setPendingMode(res.triggerMode);
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  const dirty = settings !== null && pendingMode !== null && pendingMode !== settings.triggerMode;

  const save = async (): Promise<void> => {
    if (!pendingMode) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateScanAnalysisSettings(pendingMode);
      setSettings(updated);
      setPendingMode(updated.triggerMode);
      setSavedAt(new Date().toISOString());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !settings) {
    return <p style={{ color: theme.status.error }}>Error: {error}</p>;
  }
  if (!settings || !pendingMode) return <p>Loading settings…</p>;

  return (
    <div>
      <p style={{ color: theme.text.secondary, fontSize: 13, marginTop: 0 }}>
        Controls when uploaded scans get sent through the offline AI analysis pipeline. Changes take
        effect within ~60 seconds (warm Lambdas refresh their cache on the next scan after the TTL
        expires).
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginTop: 8,
          marginBottom: 16,
        }}
      >
        {MODE_OPTIONS.map((opt) => {
          const selected = pendingMode === opt.value;
          return (
            <label
              key={opt.value}
              style={{
                display: 'block',
                padding: '12px 14px',
                border: `1px solid ${selected ? theme.brand.primary : theme.surface.border}`,
                borderRadius: theme.radius.lg,
                background: selected ? theme.surface.header : theme.surface.panel,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="triggerMode"
                value={opt.value}
                checked={selected}
                onChange={() => setPendingMode(opt.value)}
                style={{ marginRight: 8 }}
              />
              <strong style={{ fontSize: 14 }}>{opt.label}</strong>
              <div
                style={{
                  color: theme.text.secondary,
                  fontSize: 12,
                  marginTop: 4,
                  marginLeft: 22,
                }}
              >
                {opt.description}
              </div>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => void save()}
          disabled={!dirty || saving}
          style={{
            padding: '8px 16px',
            background: theme.brand.primary,
            color: theme.text.onBrand,
            border: 'none',
            borderRadius: theme.radius.md,
            fontSize: 13,
            fontWeight: 600,
            cursor: !dirty || saving ? 'not-allowed' : 'pointer',
            opacity: !dirty || saving ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {error && <span style={{ color: theme.status.error, fontSize: 13 }}>{error}</span>}
        {!error && savedAt && (
          <span style={{ color: theme.status.success, fontSize: 13 }}>
            Saved at {new Date(savedAt).toLocaleTimeString()}.
          </span>
        )}
      </div>

      {settings.updatedAt && (
        <p style={{ color: theme.text.tertiary, fontSize: 12, marginTop: 16 }}>
          Last edited {new Date(settings.updatedAt).toLocaleString()}
          {settings.updatedBy ? ` by ${settings.updatedBy}` : ''}.
        </p>
      )}
    </div>
  );
}
